import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';

/**
 * Configuration for apps available in the app switcher.
 * `path` marks an internal route - the item becomes "current" only when
 * `location.pathname` matches it. `href` is for external links.
 */
export interface AppConfig {
  id: string;
  name: string;
  icon: React.ReactNode;
  href?: string;
  path?: string;
  tag?: string;
}

/**
 * Default app list shown in the admin sidebar.
 * Empty by design - the dashboard is implicit (the whole admin area)
 * and additional apps will be added here as the suite grows.
 */
const getHolitoApps = (): AppConfig[] => [];

interface AppSwitcherProps {
  apps?: AppConfig[];
  isTextVisible?: boolean;
}

/**
 * App switcher component for navigating between holito product suite applications.
 * Displays the current app as active and provides links to other apps.
 */
export const AppSwitcher: React.FC<AppSwitcherProps> = ({
  apps,
  isTextVisible = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const appList = apps || getHolitoApps();

  // Hide the switcher entirely when there's nothing to switch to -
  // avoids leaving an empty bordered slot at the top of the sidebar.
  if (appList.length === 0) return null;

  return (
    <div className="px-2 py-3 border-b border-border">
      <div className="space-y-1" role="navigation" aria-label="App switcher">
        {appList.map((app) => {
          if (app.path) {
            const isActive = location.pathname === app.path;
            return (
              <button
                key={app.id}
                onClick={() => navigate(app.path!)}
                className={clsx(
                  'w-full text-left p-2 rounded-lg flex items-center space-x-3 cursor-pointer transition-colors',
                  isActive
                    ? 'bg-surface-hover'
                    : 'hover:bg-surface-hover group'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <span
                  className={clsx(
                    'shrink-0',
                    isActive ? 'text-volt-soft' : 'text-muted-foreground group-hover:text-foreground'
                  )}
                >
                  {app.icon}
                </span>
                {isTextVisible && (
                  <span
                    className={clsx(
                      'text-sm whitespace-nowrap',
                      isActive
                        ? 'text-foreground font-medium'
                        : 'text-foreground group-hover:text-foreground'
                    )}
                  >
                    {app.name}
                  </span>
                )}
              </button>
            );
          }

          return (
            <a
              key={app.id}
              href={app.href}
              className="w-full text-left p-2 rounded-lg hover:bg-surface-hover transition-colors group flex items-center space-x-3"
            >
              <span className="text-muted-foreground group-hover:text-foreground shrink-0">
                {app.icon}
              </span>
              {isTextVisible && (
                <span className="text-sm text-foreground group-hover:text-foreground whitespace-nowrap flex items-center gap-2">
                  {app.name}
                  {app.tag && (
                    <span className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 bg-warning/20 text-warning-soft rounded-lg border border-warning/30">
                      {app.tag}
                    </span>
                  )}
                </span>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default AppSwitcher;
